import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type { Session } from "@supabase/supabase-js";
import { useLocation } from "wouter";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { consumeIntentionalSignout } from "@/lib/intentionalSignout";
import { getLastSigninEmail } from "@/lib/lastEmail";

// Roles canoniques alignes sur backend/schemas/rbac.py UserRole.
export type Role = "ADMIN" | "ARBITRE" | "GREFFIER" | "PARTIE";

const KNOWN_ROLES: ReadonlyArray<Role> = [
  "ADMIN",
  "ARBITRE",
  "GREFFIER",
  "PARTIE",
];

interface AuthContextValue {
  session: Session | null;
  role: Role | null;
  loading: boolean;
}

const AuthContext = createContext<AuthContextValue>({
  session: null,
  role: null,
  loading: true,
});

// Le backend `get_current_user` (api/deps.py) lit dans cet ordre :
//   app_metadata.role -> user_metadata.role -> top-level `role`.
// On reproduit la meme priorite cote front.
//
// Defensive : tout acces a app_metadata / user_metadata est wrappe en
// try/catch -- une session Supabase mal formee (provider tiers, downgrade
// SDK, donnees obsoletes) ne doit JAMAIS faire crasher le rendu. Si on
// n'arrive pas a extraire un role connu, on retourne null et l'UI cache
// simplement les boutons reserves aux roles privilegies.
function extractRole(session: Session | null): Role | null {
  if (!session) return null;
  try {
    const user = session.user;
    if (!user || typeof user !== "object") return null;
    const candidates: unknown[] = [
      (user.app_metadata as Record<string, unknown> | undefined)?.role,
      (user.user_metadata as Record<string, unknown> | undefined)?.role,
      (user as unknown as { role?: unknown }).role,
    ];
    for (const c of candidates) {
      if (typeof c === "string") {
        const upper = c.toUpperCase();
        if ((KNOWN_ROLES as ReadonlyArray<string>).includes(upper)) {
          return upper as Role;
        }
      }
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[AuthContext] extractRole failed", err);
  }
  return null;
}

// Destination post-login selon le role : l'avocat (PARTIE) a son cockpit
// dedie /avocat ; arbitre/admin (ou role non resolu) -> /dashboard general.
function landingPathForRole(role: Role | null): string {
  if (role === "PARTIE") return "/avocat";
  if (role === "GREFFIER") return "/greffe";
  return "/dashboard";
}

// On ne pousse vers le tableau de bord apres SIGNED_IN que si l'utilisateur
// est sur une page publique. Evite de kicker un arbitre depuis /cases/123
// si Supabase reemet exceptionnellement un SIGNED_IN apres TOKEN_REFRESHED.
function shouldRedirectAfterSignIn(currentPath: string): boolean {
  if (currentPath === "/" || currentPath === "") return true;
  if (
    currentPath === "/mentions-legales" ||
    currentPath === "/confidentialite"
  ) {
    return true;
  }
  return false;
}

function shouldRedirectAfterSignOut(currentPath: string): boolean {
  return (
    currentPath.startsWith("/dashboard") ||
    currentPath.startsWith("/avocat") ||
    currentPath.startsWith("/cases/") ||
    currentPath.startsWith("/audiences/") ||
    currentPath.startsWith("/profil") ||
    currentPath.startsWith("/recherche")
  );
}

/**
 * Auto-recovery sur SIGNED_OUT involontaire.
 *
 * Cas vise : Safari ITP a purge le storage (au-dela de la PR #4a qui
 * resiste deja avec IndexedDB), token corrompu, refresh token revoque
 * cote Supabase, etc. L'arbitre se retrouve deconnecte sans avoir clique
 * « Deconnexion » -> l'auto-recovery lui renvoie automatiquement un
 * magic link a sa derniere adresse connue, sans qu'il ait a retaper.
 *
 * Garde-fous :
 *   - Ne se declenche QUE sur SIGNED_OUT sans flag intentionnel ;
 *   - Necessite un email memorise (sinon redirige normalement vers /) ;
 *   - Anti-spam : un guard module-scope evite que plusieurs SIGNED_OUT
 *     rapproches (lors d'un debounce navigateur, par ex.) declenchent
 *     plusieurs envois en cascade ;
 *   - Toute erreur reseau est swallowee -> degradation gracieuse vers
 *     le flow login standard.
 */
let _autoRecoveryInFlight = false;

async function tryAutoRecovery(): Promise<void> {
  if (_autoRecoveryInFlight) return;
  _autoRecoveryInFlight = true;
  try {
    const lastEmail = await getLastSigninEmail();
    if (!lastEmail) return; // jamais connecte sur ce device -> flow login classique
    const { error } = await supabase.auth.signInWithOtp({
      email: lastEmail,
      options: { emailRedirectTo: window.location.origin },
    });
    if (error) {
      // eslint-disable-next-line no-console
      console.warn("[AuthContext] auto-recovery signInWithOtp failed", error);
      toast.error(
        "Session expirée. Reconnectez-vous avec votre adresse e-mail.",
      );
      return;
    }
    toast.success(
      `Session expirée. Un nouveau lien de connexion vient d'être envoyé à ${lastEmail}.`,
      { duration: 10_000 },
    );
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[AuthContext] auto-recovery threw", err);
  } finally {
    // Petit cooldown avant de re-autoriser une tentative : evite la
    // cascade sur les pages qui re-montent vite (HMR dev, navigation
    // rapide). 5s est confortable et ne genere pas de double-mail.
    setTimeout(() => {
      _autoRecoveryInFlight = false;
    }, 5000);
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [, setLocation] = useLocation();

  useEffect(() => {
    let mounted = true;

    // Hydratation initiale : .catch + .finally garantissent qu'on quitte
    // l'etat loading meme en cas d'echec de l'API Supabase (sinon
    // ProtectedRoute reste sur un spinner perpetuel).
    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!mounted) return;
        setSession(data.session);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("[AuthContext] getSession failed", err);
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });

    const { data } = supabase.auth.onAuthStateChange((event, newSession) => {
      try {
        setSession(newSession);
        const path =
          typeof window !== "undefined" ? window.location.pathname : "/";
        if (event === "SIGNED_IN" && shouldRedirectAfterSignIn(path)) {
          // L'avocat atterrit sur son cockpit dedie ; arbitre/admin sur le
          // tableau de bord general.
          setLocation(landingPathForRole(extractRole(newSession)));
        } else if (event === "SIGNED_OUT") {
          // Discrimination volontaire vs involontaire (Safari ITP, token
          // revoque, etc.). Cf. lib/intentionalSignout.ts. L'arbitre qui
          // a clique « Deconnexion » n'a pas a recevoir de magic link
          // de recovery — il a explicitement demande a sortir.
          const wasIntentional = consumeIntentionalSignout();
          if (!wasIntentional) {
            // Auto-recovery non-bloquant : envoi magic link en background,
            // l'UI redirige normalement vers / pendant ce temps.
            void tryAutoRecovery();
          }
          if (shouldRedirectAfterSignOut(path)) {
            setLocation("/");
          }
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[AuthContext] onAuthStateChange handler failed", err);
      }
    });

    return () => {
      mounted = false;
      try {
        data.subscription.unsubscribe();
      } catch {
        /* ignore */
      }
    };
  }, [setLocation]);

  const role = extractRole(session);

  return (
    <AuthContext.Provider value={{ session, role, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function useRole(): Role | null {
  return useContext(AuthContext).role;
}

export function useIsArbiter(): boolean {
  return useRole() === "ARBITRE";
}

// Direction de procedure (ancrage Polygon, validate-step) : ADMIN doit
// pouvoir agir au meme titre que l'arbitre dans les cas d'urgence
// (continuite de service, succession). Coherent avec la matrice OHADA
// T-03 ou ADMIN porte UserScope.COMPLET.
export function useIsArbiterOrAdmin(): boolean {
  const r = useRole();
  return r === "ARBITRE" || r === "ADMIN";
}

export function useIsAdmin(): boolean {
  return useRole() === "ADMIN";
}

// Greffe (recevabilité, affectation du tribunal, enrôlement des parties) :
// prérogative du GREFFIER ; ADMIN bypasse (oversight plateforme).
export function useIsGreffeOrAdmin(): boolean {
  const r = useRole();
  return r === "GREFFIER" || r === "ADMIN";
}
